import { useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';
import type React from 'react';

import { createMandatoryContext } from '@change-corgi/core-react/context';

import type { UploadStep } from '#src/app/pages/actions/shared/types';

/**
 * Holds `uploadStep` above `CaptureVideo` (`components/Videos/index.tsx`), which mounts too deep for
 * the layout (`pages/actions/layout/index.tsx`) to read it - needed there for the V3-ask-only compact
 * header (`useIsStaticFacesAskVisible`, CHANGE-99359). Lives here rather than in
 * `components/Videos/context` so both the layout and `shared/hooks` can read it without a cross-module
 * deep import.
 */
type UploadStepContextValue = {
	uploadStep: UploadStep;
	setUploadStep: React.Dispatch<React.SetStateAction<UploadStep>>;
};

/** @knip-ignore */
export const {
	Context: UploadStepContext,
	Provider: UploadStepContextProviderInternal,
	useContext: useUploadStepContext,
} = createMandatoryContext<UploadStepContextValue>(undefined, {
	name: 'UploadStepContext',
});

const useUploadStepContextValue = (): { value: UploadStepContextValue } => {
	const [uploadStep, setUploadStep] = useState<UploadStep>('ready');

	const value = useMemo(() => ({ uploadStep, setUploadStep }), [uploadStep]);

	return { value };
};

/** Mounted in `Layout`, above `CaptureVideo`; `VideosComponentContext` reads the same value. */
export const UploadStepProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const { value } = useUploadStepContextValue();

	return <UploadStepContextProviderInternal value={value}>{children}</UploadStepContextProviderInternal>;
};
